/**
 * User endpoints for the API contract.
 *
 * These endpoints replicate the legacy Python API's user endpoints.
 * Internally they use better-auth, but expose the same interface for backwards compatibility.
 *
 * @see packages/core/api-contract/README.md for the full migration plan
 */
import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/unstable/httpapi';
import { Schema, SchemaTransformation } from 'effect';
import { UserAuthMiddleware } from './middleware';
import { UserFeatureFlags } from './user-feature-flags';
import { FEEDBACK_REPORT_TYPES } from './feedback-report';
import {
  UnauthorizedError,
  ConflictError,
  BadRequestError,
  NotFoundError,
  InternalServerError,
} from './errors';

import {
  PostLoginRequest,
  PostRegisterRequest,
  PostLogin200,
  PostRegister200,
  GetSummary200,
} from './schemas/user-dtos';

// URL params come as strings and are optional
const BooleanFromString = Schema.String.pipe(
  Schema.decodeTo(
    Schema.Boolean,
    SchemaTransformation.transform({
      decode: (s) => s === 'true',
      encode: (b) => (b ? 'true' : 'false'),
    }),
  ),
);

const RegisterUrlParams = Schema.Struct({
  random_password: Schema.optional(BooleanFromString),
});

/**
 * Profile schemas - Legacy wraps in {status, data}
 * @see submodules/backend/apps/api/listening/user/user_routes.py - get_profile, patch_profile
 * @see submodules/backend/apps/api/listening/user/user_models.py - UserProfile
 */
const UserProfileData = Schema.Struct({
  first_name: Schema.NullOr(Schema.String),
  last_name: Schema.NullOr(Schema.String),
  dob: Schema.optional(Schema.NullOr(Schema.String)),
});

const UserProfileResponse = Schema.Struct({
  status: Schema.String,
  data: UserProfileData,
});

export const UpdateProfileRequest = Schema.Struct({
  updates: Schema.Struct({
    first_name: Schema.optional(Schema.String),
    last_name: Schema.optional(Schema.String),
  }),
});

/**
 * Preferences schemas - Legacy returns preferences directly, update returns 204
 * @see submodules/backend/apps/api/listening/user/user_routes.py - get_preferences, update_preferences
 * @see submodules/backend/apps/api/listening/user/user_models.py - UserPreferences
 */
const UserPreferencesResponse = Schema.Struct({
  voice: Schema.String,
  most_recent_language: Schema.optional(Schema.Literals(['en', 'es'])),
});

export const UpdatePreferencesRequest = Schema.Struct({
  updates: Schema.Struct({
    voice: Schema.optional(Schema.String),
    most_recent_language: Schema.optional(Schema.Literals(['en', 'es'])),
  }),
});

/**
 * Login with email and password.
 * Returns a JWT token and user info.
 */
const loginEndpoint = HttpApiEndpoint.post('Login', '/login', {
  payload: PostLoginRequest,
  success: PostLogin200,
  error: [UnauthorizedError, InternalServerError],
})

  .annotate(OpenApi.Summary, 'Login with email and password')
  .annotate(
    OpenApi.Description,
    'Authenticate a user with email and password. Returns a JWT token for subsequent requests.',
  );

/**
 * Register a new user.
 * Optionally can generate a random password (for social auth flows).
 */
const registerEndpoint = HttpApiEndpoint.post('Register', '/register', {
  query: RegisterUrlParams,
  payload: PostRegisterRequest,
  success: PostRegister200,
  error: [ConflictError, BadRequestError, InternalServerError],
})

  .annotate(OpenApi.Summary, 'Register a new user')
  .annotate(
    OpenApi.Description,
    'Create a new user account. Returns a JWT token and user info. Returns 409 if email already exists, 400 for validation errors.',
  );

/**
 * Extends the legacy `GetSummary200` shape with the Stripe
 * `customer_id`. The legacy register response (`PostRegister200`)
 * exposes `customer_id` but the legacy summary response did not, even
 * though both refer to the same `user_billing.stripeCustomerId` row.
 *
 * Originally surfaced so SSR route loaders could key ProfitWell Retain
 * sessions on marketing pages. That tracker was removed 2026-07
 * (Retain not enabled on the account — WEB-ER), but the field stays:
 * the v2 contract is additive-only.
 *
 * `null` for users who have never had a Stripe billing row.
 */
const SummaryResponse = GetSummary200.pipe(
  Schema.fieldsAssign({
    customer_id: Schema.NullOr(Schema.String),
  }),
);

/**
 * Get current user summary.
 * Requires authentication.
 */
const summaryEndpoint = HttpApiEndpoint.get('Summary', '/summary', {
  success: SummaryResponse,
  error: InternalServerError,
})
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Get current user summary')
  .annotate(
    OpenApi.Description,
    'Returns the current authenticated user info including metering, preferences, profile, and Stripe customer_id.',
  );

/**
 * Re-read the caller's RevenueCat subscription now instead of waiting out the
 * summary self-heal's staleness window. Mobile calls it once per launch when
 * the RevenueCat SDK holds an entitlement the summary does not; the handler
 * only honors requests from a client that configures the SDK.
 */
const resyncBillingEndpoint = HttpApiEndpoint.post(
  'ResyncBilling',
  '/billing/resync',
  {
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Re-sync the current user billing state now');

/**
 * Get current user's profile.
 */
const getProfileEndpoint = HttpApiEndpoint.get('GetProfile', '/profile', {
  success: UserProfileResponse,
  error: InternalServerError,
})
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Get user profile');

/**
 * Update current user's profile.
 */
const updateProfileEndpoint = HttpApiEndpoint.patch(
  'UpdateProfile',
  '/profile',
  {
    payload: UpdateProfileRequest,
    success: UserProfileResponse,
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Update user profile');

/**
 * Get current user's preferences.
 */
const getPreferencesEndpoint = HttpApiEndpoint.get(
  'GetPreferences',
  '/preferences',
  {
    success: UserPreferencesResponse,
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Get user preferences');

/**
 * Update current user's preferences.
 * Legacy returns 204 No Content.
 */
const updatePreferencesEndpoint = HttpApiEndpoint.post(
  'UpdatePreferences',
  '/preferences',
  {
    payload: UpdatePreferencesRequest,
    // Legacy returns 204 No Content
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: [BadRequestError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Update user preferences');

// =============================================================================
// FCM TOKEN
// =============================================================================

const setFcmTokenEndpoint = HttpApiEndpoint.post(
  'SetFcmToken',
  '/set-fcm-token',
  {
    payload: Schema.Struct({ fcm_token: Schema.String }),
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: [ConflictError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Set FCM push notification token');

// =============================================================================
// POST-PURCHASE SURVEY
// =============================================================================

const AcquisitionSource = Schema.Literals([
  'INFLUENCER',
  'PODCAST',
  'INSTAGRAM_OR_FACEBOOK',
  'YOUTUBE_AD',
  'SEARCH_ENGINE',
  'WORD_OF_MOUTH',
  'OTHER',
]);

const postPurchaseSurveyEndpoint = HttpApiEndpoint.post(
  'PostPurchaseSurvey',
  '/post-purchase-survey',
  {
    payload: Schema.Struct({
      data: Schema.Struct({
        acquisition_source: AcquisitionSource,
        details: Schema.optional(Schema.String),
      }),
    }),
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: [ConflictError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Submit post-purchase survey');

// =============================================================================
// REFERRAL
// =============================================================================

const referralListEndpoint = HttpApiEndpoint.get(
  'ReferralList',
  '/referral-list',
  {
    success: Schema.Struct({
      referrals: Schema.Array(
        Schema.Struct({
          email: Schema.String,
          first_name: Schema.NullOr(Schema.String),
          last_name: Schema.NullOr(Schema.String),
        }),
      ),
    }),
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'List users referred by the current user');

// =============================================================================
// FEEDBACK
// =============================================================================

export const FeedbackRequest = Schema.Struct({
  user_message: Schema.String,
  report_type: Schema.optional(Schema.Literals(FEEDBACK_REPORT_TYPES)),
  audio_conversion_id: Schema.optional(Schema.String),
  paragraph_index: Schema.optional(Schema.Int),
  audio_url: Schema.optional(Schema.String),
  chapter_id: Schema.optional(Schema.String),
  raw_text: Schema.optional(Schema.String),
  timecode: Schema.optional(Schema.Finite),
});

const feedbackEndpoint = HttpApiEndpoint.post('Feedback', '/feedback', {
  payload: FeedbackRequest,
  success: Schema.Struct({ status: Schema.String }),
  error: InternalServerError,
})
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Submit feedback or report');

// =============================================================================
// POST-CANCEL SURVEY
// =============================================================================

export const PostCancelSurveyRequest = Schema.Struct({
  data: Schema.Struct({
    is_user_deleted: Schema.Boolean,
    user_cancel_reason: Schema.String,
    user_liked_features: Schema.String,
  }),
});

const postCancelSurveyEndpoint = HttpApiEndpoint.post(
  'PostCancelSurvey',
  '/post-cancel-survey',
  {
    payload: PostCancelSurveyRequest,
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Submit post-cancel survey');

// =============================================================================
// REFERRAL
// =============================================================================

const setReferredByEndpoint = HttpApiEndpoint.post(
  'SetReferredBy',
  '/set-referred-by',
  {
    payload: Schema.Struct({ referral_code: Schema.String }),
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: [BadRequestError, NotFoundError, ConflictError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Set referral code for current user');

// =============================================================================
// PASSWORD MANAGEMENT
// =============================================================================

const forgotPasswordEndpoint = HttpApiEndpoint.post(
  'ForgotPassword',
  '/forgotPassword',
  {
    payload: Schema.Struct({ email: Schema.String }),
    success: Schema.Struct({ status: Schema.String }),
    error: InternalServerError,
  },
)

  .annotate(OpenApi.Summary, 'Request password reset code');

const forgotPasswordVerifyCodeEndpoint = HttpApiEndpoint.post(
  'ForgotPasswordVerifyCode',
  '/forgotPasswordVerifyCode',
  {
    payload: Schema.Struct({
      email: Schema.String,
      code: Schema.String,
      password: Schema.String,
    }),
    success: PostLogin200,
    error: [BadRequestError, NotFoundError, InternalServerError],
  },
)

  .annotate(OpenApi.Summary, 'Verify reset code and set new password');

const updatePasswordEndpoint = HttpApiEndpoint.post(
  'UpdatePassword',
  '/update-password',
  {
    payload: Schema.Struct({
      data: Schema.Struct({
        old_password: Schema.String,
        new_password: Schema.String,
      }),
    }),
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: [UnauthorizedError, BadRequestError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Update password (authenticated)');

// =============================================================================
// DELETE USER
// =============================================================================

const featureFlagsEndpoint = HttpApiEndpoint.get(
  'GetFeatureFlags',
  '/feature-flags',
  {
    success: UserFeatureFlags,
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get the features enabled for the current user');

const deleteUserEndpoint = HttpApiEndpoint.post('DeleteUser', '/deleteUser', {
  success: Schema.Void.pipe(HttpApiSchema.status(204)),
  error: InternalServerError,
})
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Delete current user account');

export const userGroup = HttpApiGroup.make('User')
  .add(loginEndpoint)
  .add(registerEndpoint)
  .add(summaryEndpoint)
  .add(featureFlagsEndpoint)
  .add(resyncBillingEndpoint)
  .add(getProfileEndpoint)
  .add(updateProfileEndpoint)
  .add(getPreferencesEndpoint)
  .add(updatePreferencesEndpoint)
  .add(setFcmTokenEndpoint)
  .add(postPurchaseSurveyEndpoint)
  .add(feedbackEndpoint)
  .add(postCancelSurveyEndpoint)
  .add(referralListEndpoint)
  .add(setReferredByEndpoint)
  .add(forgotPasswordEndpoint)
  .add(forgotPasswordVerifyCodeEndpoint)
  .add(updatePasswordEndpoint)
  .add(deleteUserEndpoint)
  .prefix('/user');
